import React, { useState, useEffect } from 'react';
import { Trophy, Star, Zap } from 'lucide-react';
import axios from 'axios';
import './LevelSystem.css';

const LevelDisplay = ({ user, onClick, t }) => {
  const [levelData, setLevelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (user) {
      fetchLevelData();
    }
  }, [user]);

  const fetchLevelData = async () => {
    try {
      const response = await axios.get('/api/levels/my-level');
      setLevelData(response.data);
      setError(false);
    } catch (error) {
      console.error('Failed to fetch level data:', error);
      setError(true);
      // Enhanced fallback to basic level data from user object
      setLevelData({
        level: getUserLevel(),
        title: getUserLevelTitle(),
        totalExp: user?.totalExp || user?.exp || 0,
        expNeededForNext: 50,
        isMaxLevel: false,
        progressPercentage: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Get user level from available data
  const getUserLevel = () => {
    // Debug in development
    if (process.env.NODE_ENV === 'development') {
      console.log('LevelDisplay - User fields:', {
        level: user?.level,
        currentLevel: user?.currentLevel,
        totalExp: user?.totalExp,
        exp: user?.exp,
        allFields: user ? Object.keys(user) : []
      });
    }

    // Try different possible field names
    if (user?.currentLevel) return user.currentLevel;
    if (user?.level) return user.level;
    
    // Calculate from total EXP if available
    if (user?.totalExp !== undefined) {
      return calculateLevelFromExp(user.totalExp);
    }
    
    // Calculate from exp field
    if (user?.exp !== undefined) {
      return calculateLevelFromExp(user.exp);
    }
    
    return 1; // Default fallback
  };

  const getUserLevelTitle = () => {
    if (user?.levelTitle) return user.levelTitle;
    if (user?.title) return user.title;
    return getLevelTitle(getUserLevel());
  };

  // Simple level calculation based on EXP (matching server logic)
  const calculateLevelFromExp = (totalExp) => {
    if (!totalExp || totalExp < 0) return 1;
    
    // Level structure from server
    const levelStructure = [
      ...Array(5).fill(50),      // Levels 1-5: 50 EXP each
      ...Array(5).fill(75),      // Levels 6-10: 75 EXP each  
      ...Array(10).fill(100),    // Levels 11-20: 100 EXP each
      ...Array(10).fill(200),    // Levels 21-30: 200 EXP each
      ...Array(20).fill(300),    // Levels 31-50: 300 EXP each
      ...Array(30).fill(500),    // Levels 51-80: 500 EXP each
      ...Array(10).fill(1000),   // Levels 81-90: 1000 EXP each
      ...Array(10).fill(2000)    // Levels 91-100: 2000 EXP each
    ];
    
    let currentLevel = 1;
    let expUsed = 0;
    
    for (let level = 1; level <= 100; level++) {
      const expNeededForThisLevel = levelStructure[level - 1] || 0;
      
      if (expUsed + expNeededForThisLevel <= totalExp) {
        expUsed += expNeededForThisLevel;
        currentLevel = level + 1;
      } else {
        break;
      }
    }
    
    return Math.min(currentLevel, 100);
  };

  // Basic level title calculation for fallback
  const getLevelTitle = (level) => {
    if (level >= 100) return 'Legend';
    if (level >= 90) return 'Sage';
    if (level >= 80) return 'Mythic';
    if (level >= 70) return 'Grandmaster';
    if (level >= 60) return 'Master';
    if (level >= 50) return 'Expert';
    if (level >= 40) return 'Professional';
    if (level >= 30) return 'Advanced';
    if (level >= 20) return 'Intermediate';
    if (level >= 10) return 'Novice';
    return 'Beginner';
  };

  if (loading) {
    return (
      <div className="level-display">
        <Zap className="level-icon animate-spin" />
        <span className="level-number">--</span>
        <span className="level-title">{t ? t('loading', 'Loading...') : 'Loading...'}</span>
      </div>
    );
  }

  // Safety check for levelData
  if (!levelData) {
    const fallbackLevel = getUserLevel();
    const fallbackTitle = getUserLevelTitle();
    
    return (
      <div className="level-display">
        <Zap className="level-icon" />
        <span className="level-number">{fallbackLevel}</span>
        <span className="level-title">{fallbackTitle}</span>
      </div>
    );
  }

  const getLevelIcon = () => {
    const level = levelData.level || 1;
    if (level >= 90) return <Star className="level-icon" />;
    if (level >= 50) return <Trophy className="level-icon" />;
    return <Zap className="level-icon" />;
  };

  const formatExp = (exp) => {
    if (!exp && exp !== 0) return '0';
    if (exp >= 1000000) return `${(exp / 1000000).toFixed(1)}M`;
    if (exp >= 1000) return `${(exp / 1000).toFixed(1)}K`;
    return exp.toString();
  };

  const level = levelData.level || getUserLevel();
  const title = levelData.title || getUserLevelTitle();
  const totalExp = levelData.totalExp || user?.totalExp || user?.exp || 0;
  const expNeededForNext = levelData.expNeededForNext || 0;
  const isMaxLevel = levelData.isMaxLevel || level >= 100;

  const tooltipText = `${t ? t('level', 'Level') : 'Level'} ${level} - ${title}\n${formatExp(totalExp)} ${t ? t('totalExp', 'Total EXP') : 'Total EXP'}\n${isMaxLevel ? (t ? t('maxLevel', 'Max Level!') : 'Max Level!') : `${formatExp(expNeededForNext)} ${t ? t('expToNext', 'EXP to next level') : 'EXP to next level'}`}`;

  return (
    <div 
      className="level-display" 
      onClick={onClick}
      title={tooltipText}
    >
      {getLevelIcon()}
      <span className="level-number">{level}</span>
      <span className="level-title">{title}</span>
      {error && (
        <div style={{ 
          fontSize: '8px', 
          color: '#ef4444', 
          position: 'absolute', 
          top: '-8px', 
          right: '2px' 
        }}>
          !
        </div>
      )}
    </div>
  );
};

export default LevelDisplay;